import React, { useState, useEffect } from 'react';
import { Tag, Plus, Maximize2, Minimize2, Search } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useExpandable } from '../../hooks/useExpandable';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import CurrencyInput from '../../components/Admin/CurrencyInput';
import I18nTripleFields, { I18nTriple } from '../../components/Admin/I18nTripleFields';
import { emptyI18nTriple } from '../../i18n/localizeContent';
import { ActionButton, Button, IconButton } from '../../components/UI';

interface ServicoItem {
  _id: string;
  nome: string;
  i18n?: I18nTriple;
  valorPadrao: number;
  abatidoAoFecharOutros?: boolean;
  modeloContrato?: '' | 'judicial' | 'vistos';
  ativo: boolean;
}

const MODELO_CONTRATO_LABEL: Record<'judicial' | 'vistos', string> = {
  judicial: 'Judicial',
  vistos: 'Assessoria de Vistos'
};

const AdminServico: React.FC = () => {
  const [items, setItems] = useState<ServicoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ServicoItem | null>(null);
  const { isExpanded: isModalExpanded, setIsExpanded: setIsModalExpanded, toggleExpanded: toggleModalExpanded } = useExpandable();
  useEscapeKey(() => closeModal(), modalOpen);
  const [i18n, setI18n] = useState<I18nTriple>(emptyI18nTriple());
  const [valorPadrao, setValorPadrao] = useState(0);
  const [ativo, setAtivo] = useState(true);
  const [abatidoAoFecharOutros, setAbatidoAoFecharOutros] = useState(false);
  const [modeloContrato, setModeloContrato] = useState<'' | 'judicial' | 'vistos'>('');
  const [saving, setSaving] = useState(false);
  const notifications = useNotifications();
  const confirm = useConfirm();

  const fetchServicos = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      const res = await axios.get(`/servico?${params}`);
      setItems(res.data);
    } catch (err) {
      console.error(err);
      notifications.error.network();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServicos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const openNew = () => {
    setEditing(null);
    setI18n(emptyI18nTriple());
    setValorPadrao(0);
    setAtivo(true);
    setAbatidoAoFecharOutros(false);
    setModeloContrato('');
    setModalOpen(true);
  };

  const openEdit = (item: ServicoItem) => {
    setEditing(item);
    setI18n({
      pt: { nome: item.i18n?.pt?.nome || item.nome },
      en: { nome: item.i18n?.en?.nome || '' },
      es: { nome: item.i18n?.es?.nome || '' }
    });
    setValorPadrao(item.valorPadrao);
    setAtivo(item.ativo);
    setAbatidoAoFecharOutros(!!item.abatidoAoFecharOutros);
    setModeloContrato(item.modeloContrato || '');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setIsModalExpanded(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ptNome = i18n.pt?.nome?.trim();
    if (!ptNome) return;
    try {
      setSaving(true);
      const payload = { nome: ptNome, i18n, valorPadrao, ativo, abatidoAoFecharOutros, modeloContrato };
      if (editing) {
        await axios.put(`/servico/${editing._id}`, payload);
      } else {
        await axios.post('/servico', payload);
      }
      notifications.success.settingsUpdated();
      closeModal();
      fetchServicos();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(error.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: 'Excluir serviço', message: 'Este serviço será removido do catálogo.', confirmText: 'Excluir', variant: 'danger' }))) return;
    try {
      await axios.delete(`/servico/${id}`);
      notifications.success.settingsUpdated();
      fetchServicos();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(error.response?.data?.message || 'Erro ao excluir');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <Breadcrumbs />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Serviços</h1>
            <p className="text-gray-600">
              Catálogo de serviços com nome (em pt/en/es) e valor padrão, para incluir rapidamente num orçamento.
            </p>
          </div>
          <Button className="whitespace-nowrap shrink-0" type="button" onClick={openNew}>
            <Plus className="w-4 h-4" />
            Novo serviço
          </Button>
        </div>

        <div className="mt-4 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar serviço por nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="field pl-10 pr-4"
          />
        </div>

        {loading ? (
          <div className="mt-8 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="mt-8 card text-center py-12 text-gray-500">Nenhum serviço cadastrado.</div>
        ) : (
          <div className="mt-6 space-y-3">
            {items.map((item) => (
              <div
                key={item._id}
                className="card flex flex-col md:flex-row md:items-center md:justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Tag className="w-4 h-4 text-primary-600 shrink-0" />
                    <span className="font-medium text-gray-900">{item.nome}</span>
                    {!item.ativo && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Inativo</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    €{Number(item.valorPadrao).toFixed(2)}
                    {item.abatidoAoFecharOutros && (
                      <span className="text-green-700"> · abatido se fechar outros serviços</span>
                    )}
                    {item.modeloContrato && (
                      <span> · contrato: {MODELO_CONTRATO_LABEL[item.modeloContrato]}</span>
                    )}
                  </p>
                </div>
                <div className="row-actions md:shrink-0">
                  <ActionButton
                    action="edit"
                    onClick={() => openEdit(item)}
                  />
                  <ActionButton
                    action="delete"
                    onClick={() => handleDelete(item._id)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full overflow-y-auto p-6 transition-all duration-200 ${
            isModalExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-lg max-h-[90vh]'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">{editing ? 'Editar serviço' : 'Novo serviço'}</h2>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={toggleModalExpanded}
                  className="icon-btn p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title={isModalExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isModalExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>
                <IconButton action="close" onClick={closeModal} />
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <I18nTripleFields mode="servico" value={i18n} onChange={setI18n} />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor padrão (€)</label>
                <CurrencyInput
                  value={valorPadrao}
                  onChange={setValorPadrao}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Modelo de contrato</label>
                <select
                  value={modeloContrato}
                  onChange={(e) => setModeloContrato(e.target.value as '' | 'judicial' | 'vistos')}
                  className="field field--select"
                >
                  <option value="">— Não define —</option>
                  <option value="judicial">Judicial</option>
                  <option value="vistos">Assessoria de Vistos</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  O contrato de um orçamento com este serviço já abre com este modelo.
                </p>
              </div>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
                Ativo
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={abatidoAoFecharOutros}
                  onChange={(e) => setAbatidoAoFecharOutros(e.target.checked)}
                />
                <span>
                  Abater do total se o cliente fechar outros serviços
                  <span className="block text-xs text-gray-500">
                    Ex.: Análise Personalizada — cobrada sozinha, mas sai do total quando o orçamento tem outros serviços.
                  </span>
                </span>
              </label>
              <div className="form-actions pt-2">
                <Button variant="ghost" size="sm"
                  type="button"
                  onClick={closeModal}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServico;
